// Estado e acoes dos Banners do Cardapio.
// Mantem a lista em memoria e recarrega do servidor depois de cada escrita —
// a ordem e o display_order vem do backend, entao reler evita a lista da tela
// divergir do que o cliente ve no cardapio.

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { bannersApi } from "../api/bannersApi";
import { ehVideo, lerVideoBanner } from "../lib/videoBanner";

// Espelha os maximos do backend (api/v1/cardapio_banners/service.py).
// Aqui serve pra desabilitar o botao antes do envio; quem manda de verdade
// e o servidor, que devolve 400 se passar.
const MAXIMOS = { cardapio: 4, totem: 5, totem_cardapio: 5 };

/** Mensagem de erro da API, com fallback pra frase util (nunca "[object Object]"). */
function mensagemErro(err, padrao) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : padrao;
}

/** placement: "cardapio" (padrão) ou "totem" — cada um com lista, limite e upload próprios. */
export function useBanners(placement = "cardapio") {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const ehTotem = placement === "totem"; // mídias (foto/vídeo) da tela de descanso
  const ehBannerTotem = placement === "totem_cardapio"; // imagens largas do topo do cardápio do totem
  const MAX_BANNERS = MAXIMOS[placement] || MAXIMOS.cardapio;

  const carregar = useCallback(async () => {
    try {
      const res = await bannersApi.listar(placement);
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(mensagemErro(err, "Não foi possível carregar os banners."));
    } finally {
      setLoading(false);
    }
  }, [placement]);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionar = useCallback(async (file) => {
    if (!file) return;
    if (banners.length >= MAX_BANNERS) {
      toast.error(`Limite de ${MAX_BANNERS} ${ehTotem ? "mídias" : "banners"} atingido.`);
      return;
    }
    const video = placement === "cardapio" && ehVideo(file);
    if (video && banners.some((b) => b.media_type === "video")) {
      toast.error("Já existe um banner em vídeo. Remova o atual antes de enviar outro.");
      return;
    }
    setEnviando(true);
    try {
      // Vídeo do topo do cardápio: capa tirada aqui (1º quadro); sobe o vídeo
      // primeiro (é o que mais falha) e só depois a capa — nada fica órfão
      let posterUrl = null;
      const capa = video ? await lerVideoBanner(file) : null;
      const up = ehTotem
        ? await bannersApi.enviarMidiaTotem(file)
        : video ? await bannersApi.enviarVideo(file) : await bannersApi.enviarImagem(file);
      if (capa) posterUrl = (await bannersApi.enviarImagem(capa))?.data?.image_url;
      const imageUrl = up?.data?.image_url;
      if (!imageUrl) throw new Error("upload sem URL");
      await bannersApi.criar({
        image_url: imageUrl, title: "", enabled: true,
        placement, media_type: up?.data?.media_type || "image", poster_url: posterUrl,
      });
      await carregar();
      toast.success(ehTotem ? "Mídia adicionada ao totem." : ehBannerTotem ? "Banner adicionado ao cardápio do totem." : "Banner adicionado ao cardápio.");
    } catch (err) {
      toast.error(mensagemErro(err, err?.paraOLojista ? err.message : "Não foi possível enviar o arquivo."));
    } finally {
      setEnviando(false);
    }
  }, [banners, carregar, placement, ehTotem, ehBannerTotem, MAX_BANNERS]);

  const alternarAtivo = useCallback(async (banner) => {
    // Otimista: o switch responde na hora e volta atras se a API recusar.
    const proximo = !banner.enabled;
    setBanners((atual) =>
      atual.map((b) => (b.id === banner.id ? { ...b, enabled: proximo } : b))
    );
    try {
      await bannersApi.atualizar(banner.id, { enabled: proximo });
    } catch (err) {
      setBanners((atual) =>
        atual.map((b) => (b.id === banner.id ? { ...b, enabled: !proximo } : b))
      );
      toast.error(mensagemErro(err, "Não foi possível alterar o banner."));
    }
  }, []);

  const remover = useCallback(async (banner) => {
    try {
      await bannersApi.deletar(banner.id);
      setBanners((atual) => atual.filter((b) => b.id !== banner.id));
      toast.success("Banner removido.");
    } catch (err) {
      toast.error(mensagemErro(err, "Não foi possível remover o banner."));
    }
  }, []);

  const mover = useCallback(async (banner, direcao) => {
    const idx = banners.findIndex((b) => b.id === banner.id);
    const destino = idx + direcao;
    if (idx < 0 || destino < 0 || destino >= banners.length) return;
    const reordenados = [...banners];
    [reordenados[idx], reordenados[destino]] = [reordenados[destino], reordenados[idx]];
    setBanners(reordenados);
    try {
      await bannersApi.reordenar(reordenados.map((b) => b.id), placement);
    } catch (err) {
      setBanners(banners);
      toast.error(mensagemErro(err, "Não foi possível salvar a ordem."));
    }
  }, [banners, placement]);

  return {
    banners, loading, enviando, maximo: MAX_BANNERS,
    adicionar, alternarAtivo, remover, mover,
  };
}
